import {
  createContext,
  useContext,
  useState,
  useCallback,
  type ReactNode,
  type ReactElement,
} from "react";
import axios, { type AxiosError } from "axios";

import type {
  Series,
  SeriesBook,
  Character,
  SeriesLimit,
  CharacterAddRequest,
  CharacterUpdateRequest,
  SeriesCreateRequest,
  SeriesUpdateRequest,
} from "@/types/series";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/v1/series`;

/**
 * Series context for story generation
 */
export interface SeriesGenerationContext {
  series_id: string;
  series_title: string;
  characters: Array<{
    name: string;
    description: string;
    role: string;
    portrait_url?: string;
  }>;
  setting?: string;
  tone?: string;
}

/**
 * API response types
 */
interface UserSeriesResponse {
  series: Series[];
  total: number;
  max_series: number;
  is_premium: boolean;
}

interface ValidationError {
  msg?: string;
  message?: string;
}

interface ErrorResponse {
  detail?: string | ValidationError[];
}

/**
 * Series context value type
 */
export interface SeriesContextValue {
  // State
  seriesList: Series[];
  currentSeries: Series | null;
  seriesBooks: SeriesBook[];
  loading: boolean;
  error: string | null;
  seriesLimit: SeriesLimit;

  // Actions
  fetchUserSeries: () => Promise<Series[]>;
  fetchSeries: (seriesId: string) => Promise<Series | null>;
  createSeries: (seriesData: SeriesCreateRequest) => Promise<Series>;
  updateSeries: (
    seriesId: string,
    updateData: SeriesUpdateRequest
  ) => Promise<Series>;
  deleteSeries: (seriesId: string) => Promise<boolean>;
  addCharacter: (
    seriesId: string,
    characterData: CharacterAddRequest
  ) => Promise<Character>;
  updateCharacter: (
    seriesId: string,
    characterId: string,
    updateData: CharacterUpdateRequest
  ) => Promise<Series>;
  deleteCharacter: (seriesId: string, characterId: string) => Promise<boolean>;
  getSeriesContext: (
    seriesId: string
  ) => Promise<SeriesGenerationContext | null>;
  canCreateSeries: () => boolean;
  clearCurrentSeries: () => void;
  clearError: () => void;
}

/**
 * Series provider props
 */
interface SeriesProviderProps {
  children: ReactNode;
}

const SeriesContext = createContext<SeriesContextValue | null>(null);

export const SeriesProvider = ({
  children,
}: SeriesProviderProps): ReactElement => {
  const [seriesList, setSeriesList] = useState<Series[]>([]);
  const [currentSeries, setCurrentSeries] = useState<Series | null>(null);
  const [seriesBooks, setSeriesBooks] = useState<SeriesBook[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [seriesLimit, setSeriesLimit] = useState<SeriesLimit>({
    total: 0,
    max: 1,
    isPremium: false,
  });

  // Clear error
  const clearError = useCallback((): void => setError(null), []);

  // Helper to extract error message from response
  const extractErrorMessage = (
    err: AxiosError<ErrorResponse>,
    fallback: string
  ): string => {
    const detail = err.response?.data?.detail;
    if (!detail) return fallback;
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail)) {
      // Pydantic validation errors format
      return detail
        .map((e) => e.msg || e.message || JSON.stringify(e))
        .join(", ");
    }
    return fallback;
  };

  // Fetch all series for user
  const fetchUserSeries = useCallback(async (): Promise<Series[]> => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get<UserSeriesResponse>(API);
      setSeriesList(response.data.series || []);
      setSeriesLimit({
        total: response.data.total || 0,
        max: response.data.max_series || 1,
        isPremium: response.data.is_premium || false,
      });
      return response.data.series;
    } catch (err) {
      const axiosError = err as AxiosError<ErrorResponse>;
      const message =
        axiosError.response?.data?.detail || "Failed to fetch series";
      setError(typeof message === "string" ? message : "Failed to fetch series");
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch single series with books
  const fetchSeries = useCallback(
    async (seriesId: string): Promise<Series | null> => {
      setLoading(true);
      setError(null);
      try {
        const response = await axios.get<Series>(`${API}/${seriesId}`);
        setCurrentSeries(response.data);
        setSeriesBooks(response.data.books || []);
        return response.data;
      } catch (err) {
        const axiosError = err as AxiosError<ErrorResponse>;
        const message =
          axiosError.response?.data?.detail || "Failed to fetch series";
        setError(
          typeof message === "string" ? message : "Failed to fetch series"
        );
        return null;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  // Create a new series
  const createSeries = useCallback(
    async (seriesData: SeriesCreateRequest): Promise<Series> => {
      setLoading(true);
      setError(null);
      try {
        const response = await axios.post<Series>(API, seriesData);
        const newSeries = response.data;
        setSeriesList((prev) => [newSeries, ...prev]);
        setSeriesLimit((prev) => ({ ...prev, total: prev.total + 1 }));
        return newSeries;
      } catch (err) {
        const axiosError = err as AxiosError<ErrorResponse>;
        const message = extractErrorMessage(
          axiosError,
          "Failed to create series"
        );
        setError(message);
        throw new Error(message);
      } finally {
        setLoading(false);
      }
    },
    []
  );

  // Update a series
  const updateSeries = useCallback(
    async (
      seriesId: string,
      updateData: SeriesUpdateRequest
    ): Promise<Series> => {
      setLoading(true);
      setError(null);
      try {
        const response = await axios.patch<Series>(
          `${API}/${seriesId}`,
          updateData
        );
        const updatedSeries = response.data;
        setSeriesList((prev) =>
          prev.map((s) => (s.id === seriesId ? updatedSeries : s))
        );
        if (currentSeries?.id === seriesId) {
          setCurrentSeries(updatedSeries);
        }
        return updatedSeries;
      } catch (err) {
        const axiosError = err as AxiosError<ErrorResponse>;
        const message = extractErrorMessage(
          axiosError,
          "Failed to update series"
        );
        setError(message);
        throw new Error(message);
      } finally {
        setLoading(false);
      }
    },
    [currentSeries?.id]
  );

  // Delete a series
  const deleteSeries = useCallback(
    async (seriesId: string): Promise<boolean> => {
      setLoading(true);
      setError(null);
      try {
        await axios.delete(`${API}/${seriesId}`);
        setSeriesList((prev) => prev.filter((s) => s.id !== seriesId));
        setSeriesLimit((prev) => ({
          ...prev,
          total: Math.max(0, prev.total - 1),
        }));
        if (currentSeries?.id === seriesId) {
          setCurrentSeries(null);
          setSeriesBooks([]);
        }
        return true;
      } catch (err) {
        const axiosError = err as AxiosError<ErrorResponse>;
        const message = extractErrorMessage(
          axiosError,
          "Failed to delete series"
        );
        setError(message);
        throw new Error(message);
      } finally {
        setLoading(false);
      }
    },
    [currentSeries?.id]
  );

  // Add a character to series
  const addCharacter = useCallback(
    async (
      seriesId: string,
      characterData: CharacterAddRequest
    ): Promise<Character> => {
      setError(null);
      try {
        const response = await axios.post<Character>(
          `${API}/${seriesId}/characters`,
          characterData
        );
        // Refresh the series to get updated characters
        await fetchSeries(seriesId);
        return response.data;
      } catch (err) {
        const axiosError = err as AxiosError<ErrorResponse>;
        const message = extractErrorMessage(
          axiosError,
          "Failed to add character"
        );
        setError(message);
        throw new Error(message);
      }
    },
    [fetchSeries]
  );

  // Update a character
  const updateCharacter = useCallback(
    async (
      seriesId: string,
      characterId: string,
      updateData: CharacterUpdateRequest
    ): Promise<Series> => {
      setError(null);
      try {
        const response = await axios.patch<Series>(
          `${API}/${seriesId}/characters/${characterId}`,
          updateData
        );
        if (currentSeries?.id === seriesId) {
          setCurrentSeries(response.data);
        }
        return response.data;
      } catch (err) {
        const axiosError = err as AxiosError<ErrorResponse>;
        const message = extractErrorMessage(
          axiosError,
          "Failed to update character"
        );
        setError(message);
        throw new Error(message);
      }
    },
    [currentSeries?.id]
  );

  // Delete a character
  const deleteCharacter = useCallback(
    async (seriesId: string, characterId: string): Promise<boolean> => {
      setError(null);
      try {
        await axios.delete(`${API}/${seriesId}/characters/${characterId}`);
        // Refresh the series to get updated characters
        await fetchSeries(seriesId);
        return true;
      } catch (err) {
        const axiosError = err as AxiosError<ErrorResponse>;
        const message = extractErrorMessage(
          axiosError,
          "Failed to delete character"
        );
        setError(message);
        throw new Error(message);
      }
    },
    [fetchSeries]
  );

  // Get series context for book generation
  const getSeriesContext = useCallback(
    async (seriesId: string): Promise<SeriesGenerationContext | null> => {
      try {
        const response = await axios.get<SeriesGenerationContext>(
          `${API}/${seriesId}/context`
        );
        return response.data;
      } catch (err) {
        const axiosError = err as AxiosError<ErrorResponse>;
        const message =
          axiosError.response?.data?.detail || "Failed to get series context";
        setError(
          typeof message === "string" ? message : "Failed to get series context"
        );
        return null;
      }
    },
    []
  );

  // Check if user can create more series
  const canCreateSeries = useCallback((): boolean => {
    if (seriesLimit.isPremium) return true;
    return seriesLimit.total < seriesLimit.max;
  }, [seriesLimit]);

  // Clear current series selection
  const clearCurrentSeries = useCallback((): void => {
    setCurrentSeries(null);
    setSeriesBooks([]);
  }, []);

  const value: SeriesContextValue = {
    // State
    seriesList,
    currentSeries,
    seriesBooks,
    loading,
    error,
    seriesLimit,

    // Actions
    fetchUserSeries,
    fetchSeries,
    createSeries,
    updateSeries,
    deleteSeries,
    addCharacter,
    updateCharacter,
    deleteCharacter,
    getSeriesContext,
    canCreateSeries,
    clearCurrentSeries,
    clearError,
  };

  return (
    <SeriesContext.Provider value={value}>{children}</SeriesContext.Provider>
  );
};

export const useSeries = (): SeriesContextValue => {
  const context = useContext(SeriesContext);
  if (!context) {
    throw new Error("useSeries must be used within a SeriesProvider");
  }
  return context;
};

export default SeriesContext;
